import { CarDeadlineStateDto, CarIssueSeverityDto, CarLogEntryKindDto } from '@/api/generated';

export type CarsMaintenanceTranslation = {
  attention: {
    allClear: string;
    collapseInfo: string;
    critical: string;
    expandInfo: string;
    info: string;
    infoHint: string;
    labels: {
      grounded: string;
      missing: string;
      openIssue: string;
      overdueDate: string;
      overdueMileage: string;
      plannedDatePassed: string;
      soonDate: string;
      soonMileage: string;
    };
    loadError: string;
    onlyMine: string;
    title: string;
    warning: string;
  };
  deadlineDialog: {
    dueMileage: string;
    dueMileageHint: string;
    notApplicable: string;
    title: string;
    validUntil: string;
  };
  deadlines: {
    actions: {
      cancelPlan: string;
      changePlan: string;
      done: string;
      edit: string;
      fill: string;
      more: string;
      notApplicable: string;
      notApplicableForCar: string;
      plan: string;
    };
    cancelDialog: {
      confirm: string;
      content: string;
      title: string;
    };
    dueMileage: string;
    empty: string;
    notApplicableDialog: {
      confirm: string;
      content: string;
      title: string;
    };
    notApplicableTitle: string;
    planned: string;
    remainingMileage: string;
    restore: string;
    states: Record<CarDeadlineStateDto, string>;
    title: string;
    validUntil: string;
  };
  form: {
    cancel: string;
    save: string;
  };
  issueDialog: {
    createTitle: string;
    description: string;
    editTitle: string;
    severity: string;
  };
  issues: {
    actions: {
      delete: string;
      edit: string;
      resolve: string;
    };
    deleteDialog: {
      confirm: string;
      content: string;
      title: string;
    };
    empty: string;
    report: string;
    reported: string;
    resolved: string;
    resolvedTitle: string;
    severity: Record<CarIssueSeverityDto, string>;
    title: string;
  };
  logDialog: {
    cost: string;
    createTitle: string;
    description: string;
    editTitle: string;
    entryDate: string;
    kind: string;
    mileage: string;
    mileageHint: string;
    renewal: string;
    renewalHint: string;
    renewalType: string;
    renewDueMileage: string;
    renewValidUntil: string;
    resolvedIssue: string;
    resolvedIssueHint: string;
    workshop: string;
  };
  logEntries: {
    actions: {
      add: string;
      delete: string;
      edit: string;
      files: string;
    };
    deleteDialog: {
      confirm: string;
      content: string;
      title: string;
    };
    empty: string;
    head: {
      cost: string;
      date: string;
      description: string;
      kind: string;
      mileage: string;
    };
    kinds: Record<CarLogEntryKindDto, string>;
    renewed: string;
    summary: string;
    title: string;
  };
  logFiles: {
    addedAt: string;
    addedBy: string;
    close: string;
    description: string;
    empty: string;
    name: string;
    size: string;
    title: string;
  };
  planDialog: {
    assignee: string;
    assigneeHint: string;
    note: string;
    notePlaceholder: string;
    plannedDate: string;
    title: string;
  };
  relative: {
    daysAgo: string;
    inDays: string;
    today: string;
    tomorrow: string;
    yesterday: string;
  };
  validation: {
    costFormat: string;
    dateOrMileage: string;
    futureDate: string;
    mileageRange: string;
  };
};

export const carsMaintenanceTranslation: CarsMaintenanceTranslation = {
  attention: {
    allClear: 'Wszystkie aktywne auta mają aktualne terminy i żadnych otwartych usterek.',
    collapseInfo: 'Ukryj',
    critical: 'Pilne: {{count}}',
    expandInfo: 'Pokaż',
    info: 'Do uzupełnienia: {{count}}',
    infoHint: 'Terminy bez daty — wpisz datę ważności albo oznacz, że termin auta nie dotyczy.',
    labels: {
      grounded: 'Nie jeździć: {{description}}',
      missing: '{{type}}: brak daty',
      openIssue: 'Usterka: {{description}}',
      overdueDate: '{{type}}: termin minął {{when}}',
      overdueMileage: '{{type}}: przebieg przekroczony o {{km}} km',
      plannedDatePassed: '{{type}}: wizyta {{date}} minęła, dodaj wpis do dziennika',
      soonDate: '{{type}}: {{when}}, nie zaplanowano',
      soonMileage: '{{type}}: zostało {{km}} km, nie zaplanowano',
    },
    loadError: 'Nie udało się sprawdzić terminów i usterek aut. Odśwież stronę za chwilę.',
    onlyMine: 'Tylko moje auta',
    title: 'Wymaga uwagi',
    warning: 'Do zrobienia: {{count}}',
  },
  deadlineDialog: {
    dueMileage: 'Przy przebiegu (km)',
    dueMileageHint: 'Dla terminów liczonych przebiegiem, np. serwisu co 30 000 km.',
    notApplicable: 'Ten termin nie dotyczy auta',
    title: '{{type}} — termin',
    validUntil: 'Ważny do',
  },
  deadlines: {
    actions: {
      cancelPlan: 'Odwołaj wizytę',
      changePlan: 'Zmień termin wizyty',
      done: 'Wykonano',
      edit: 'Edytuj termin',
      fill: 'Uzupełnij',
      more: 'Więcej',
      notApplicable: 'Nie dotyczy',
      notApplicableForCar: 'Nie dotyczy tego auta',
      plan: 'Zaplanuj',
    },
    cancelDialog: {
      confirm: 'Odwołaj',
      content: 'Wizyta zniknie z karty, a jej zadanie zostanie anulowane.',
      title: 'Odwołać wizytę?',
    },
    dueMileage: 'Przy {{km}} km',
    empty: 'Brak typów terminów. Dodaj je w Słownikach jako „Terminy aut”.',
    notApplicableDialog: {
      confirm: 'Nie dotyczy',
      content: 'Zaplanowana wizyta zostanie odwołana, a jej zadanie anulowane.',
      title: 'Termin nie dotyczy tego auta?',
    },
    notApplicableTitle: 'Nie dotyczy tego auta:',
    planned: 'Wizyta {{date}}',
    remainingMileage: 'zostało {{km}} km',
    restore: 'Przywróć termin',
    states: {
      [CarDeadlineStateDto.DueSoon]: 'Nie zaplanowany',
      [CarDeadlineStateDto.Missing]: 'Brak daty',
      [CarDeadlineStateDto.NotApplicable]: 'Nie dotyczy',
      [CarDeadlineStateDto.Ok]: 'Aktualny',
      [CarDeadlineStateDto.Overdue]: 'Po terminie',
      [CarDeadlineStateDto.Planned]: 'Zaplanowany',
      [CarDeadlineStateDto.PlannedDatePassed]: 'Wizyta minęła',
    },
    title: 'Terminy',
    validUntil: 'Ważny do {{date}}',
  },
  form: {
    cancel: 'Anuluj',
    save: 'Zapisz',
  },
  issueDialog: {
    createTitle: 'Zgłoś usterkę',
    description: 'Co się dzieje?',
    editTitle: 'Usterka',
    severity: 'Czy auto może jeździć?',
  },
  issues: {
    actions: {
      delete: 'Usuń',
      edit: 'Edytuj',
      resolve: 'Naprawiona',
    },
    deleteDialog: {
      confirm: 'Usuń',
      content: 'Usuń tylko usterkę zgłoszoną omyłkowo. Naprawioną zamknij wpisem w dzienniku.',
      title: 'Usunąć usterkę?',
    },
    empty: 'Brak otwartych usterek.',
    report: 'Zgłoś usterkę',
    reported: 'Zgłoszenie: {{name}}, {{date}}',
    resolved: 'Naprawiona {{date}}',
    resolvedTitle: 'Naprawione',
    severity: {
      [CarIssueSeverityDto.CanDrive]: 'Może jeździć',
      [CarIssueSeverityDto.Grounded]: 'Nie jeździć',
    },
    title: 'Usterki',
  },
  logDialog: {
    cost: 'Koszt netto',
    createTitle: 'Nowy wpis w dzienniku',
    description: 'Co zostało zrobione?',
    editTitle: 'Wpis w dzienniku',
    entryDate: 'Data',
    kind: 'Rodzaj',
    mileage: 'Przebieg (km)',
    mileageHint: 'Wyższy przebieg od zapisanego zaktualizuje licznik auta.',
    renewal: 'Odnów termin',
    renewalHint:
      'Wpisz nową datę ważności albo przebieg kolejnej wizyty. Zaplanowana wizyta zniknie, a jej zadanie się zakończy.',
    renewalType: 'Termin',
    renewDueMileage: 'Następny przy przebiegu (km)',
    renewValidUntil: 'Nowa data ważności',
    resolvedIssue: 'Naprawiona usterka',
    resolvedIssueHint: 'Usterka zniknie z otwartych.',
    workshop: 'Warsztat / wykonawca',
  },
  logEntries: {
    actions: {
      add: 'Dodaj wpis',
      delete: 'Usuń',
      edit: 'Edytuj',
      files: 'Faktury i pliki',
    },
    deleteDialog: {
      confirm: 'Usuń',
      content: 'Odnowiony termin i zamknięta usterka zostaną bez zmian.',
      title: 'Usunąć wpis z dziennika?',
    },
    empty: 'Dziennik jest pusty. Dodaj pierwszy wpis — przegląd, serwis albo naprawę.',
    head: {
      cost: 'Koszt netto',
      date: 'Data',
      description: 'Opis',
      kind: 'Rodzaj',
      mileage: 'Przebieg',
    },
    kinds: {
      [CarLogEntryKindDto.Damage]: 'Szkoda',
      [CarLogEntryKindDto.Inspection]: 'Przegląd',
      [CarLogEntryKindDto.Other]: 'Inne',
      [CarLogEntryKindDto.Repair]: 'Naprawa',
      [CarLogEntryKindDto.Service]: 'Serwis',
      [CarLogEntryKindDto.Tires]: 'Opony',
    },
    renewed: 'odnowiono: {{type}}',
    summary: 'W {{year}} r.: wpisów {{count}}, koszt {{cost}} netto',
    title: 'Dziennik napraw i serwisu',
  },
  logFiles: {
    addedAt: 'Dodano',
    addedBy: 'Dodał(a)',
    close: 'Zamknij',
    description: 'Dołącz fakturę, protokół albo zdjęcia do tego wpisu.',
    empty: 'Brak plików.',
    name: 'Nazwa',
    size: 'Rozmiar',
    title: 'Pliki wpisu z {{date}}',
  },
  planDialog: {
    assignee: 'Kto się zajmie',
    assigneeHint: 'Ta osoba dostanie zadanie z datą wizyty przypięte do auta.',
    note: 'Notatka',
    notePlaceholder: 'Warsztat, godzina, kontakt',
    plannedDate: 'Data wizyty',
    title: '{{type}} — zaplanuj wizytę',
  },
  relative: {
    daysAgo: '{{days}} dni temu',
    inDays: 'za {{days}} dni',
    today: 'dziś',
    tomorrow: 'jutro',
    yesterday: 'wczoraj',
  },
  validation: {
    costFormat: 'Podaj kwotę, np. 1420 albo 199,99.',
    dateOrMileage: 'Podaj nową datę ważności albo przebieg.',
    futureDate: 'Data wpisu nie może być z przyszłości.',
    mileageRange: 'Przebieg musi mieścić się między 0 a 2 000 000 km.',
  },
};
